import * as _ from 'lodash';
import React from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  ReferenceLine,
} from 'recharts';

import 'source/packages/styles/common.scss';

import { Util } from '@finley-technologies/finley-types';

import {
  CohortCadence,
  cohortSeriesCadenceLabelFormatter,
  findVintageChartDataForCohortSeriesAge,
  PortfolioChartConfig,
  valueTypeToFormatterMap,
} from './portfolioChartUtils';
import { Props as DefaultLegendContentProps } from 'recharts/types/component/DefaultLegendContent';
import { colors } from '~/packages/common-frontend/colors';

type ChartData = { cohortSeriesAge: number; [key: string]: Util.Primitive };
type PortfolioVintageReChartProps<T extends ChartData> = {
  chartData: T[];
  chartTitle: string;
  selectedCohortSeriesCadence: CohortCadence;
  currentSelectedCohortSeriesAge: number;
  onSelectDatapoint: (selectedDatapoint: T) => void;
  chartConfig: PortfolioChartConfig<any>;
  visibleChartComponents: (keyof T)[];
  renderCustomLegend?: (props: DefaultLegendContentProps) => React.ReactElement;
  yAxisLabel?: string;
  showLegend?: boolean;
};

const yAxisTickCount = 6;

export function PortfolioVintageReChart<T extends ChartData>({
  chartData,
  chartTitle,
  currentSelectedCohortSeriesAge,
  onSelectDatapoint,
  selectedCohortSeriesCadence,
  chartConfig,
  visibleChartComponents,
  renderCustomLegend,
  yAxisLabel,
  showLegend = true,
}: PortfolioVintageReChartProps<T>) {
  // do not consider 'boolean' as a chart format to display as an axis
  const chartConfigFormatsForAxes = _.chain(chartConfig)
    .map((config) => config && config.format)
    .filter((format) => format !== 'boolean')
    .compact()
    .uniq()
    .value();

  const xAxisLabel = cohortSeriesCadenceLabelFormatter(
    selectedCohortSeriesCadence,
  );
  return (
    <ResponsiveContainer width="100%" maxHeight={650}>
      <ComposedChart
        data={chartData}
        margin={{ top: 20, right: 30, left: 30, bottom: 0 }}
      >
        <CartesianGrid vertical={false} />
        {currentSelectedCohortSeriesAge ? (
          <ReferenceLine
            x={currentSelectedCohortSeriesAge}
            isFront
            stroke={colors.aurora[400]}
            yAxisId={chartConfigFormatsForAxes[0]}
          />
        ) : null}
        <XAxis
          dataKey="cohortSeriesAge"
          height={100}
          tick={{ fontWeight: 600 }}
          axisLine={false}
          tickLine={false}
          tickMargin={10}
          cursor="pointer"
          onClick={(clickedSeriesAgeProps: any) => {
            // must type onClick prop as 'any' because the library's type for this is a React MouseEvent which does not carry the 'value' key
            // but this event prop in reality does carry the 'value' key
            const chartDataForCohortSeriesAge = findVintageChartDataForCohortSeriesAge(
              chartData,
              clickedSeriesAgeProps.value,
            );
            if (chartDataForCohortSeriesAge) {
              onSelectDatapoint(chartDataForCohortSeriesAge);
            }
          }}
          label={{
            value: xAxisLabel,
            style: { fontWeight: 600, fill: 'black' },
          }}
        />

        <YAxis
          tickFormatter={valueTypeToFormatterMap[chartConfigFormatsForAxes[0]]}
          tick={{ fontWeight: 600 }}
          axisLine={false}
          tickLine={false}
          tickCount={yAxisTickCount}
          yAxisId={chartConfigFormatsForAxes[0]}
          orientation="left"
          label={
            yAxisLabel && {
              value: yAxisLabel,
              angle: -90,
              position: 'insideLeft',
              style: {
                textAnchor: 'middle',
                fontWeight: 600,
                fill: 'black',
              },
            }
          }
          width={100}
        />
        {showLegend && (
          <Legend
            formatter={(value: string) => {
              const chartConfigForField = chartConfig[value];
              const legendText = chartConfigForField
                ? chartConfigForField.label
                : _.startCase(value);
              return (
                <span
                  style={{
                    color: 'black',
                    fontSize: '14px',
                    fontWeight: 500,
                    verticalAlign: 'text-top',
                    margin: '0 40px 0 16px',
                  }}
                >
                  {legendText}
                </span>
              );
            }}
            iconSize={20}
            align="left"
            wrapperStyle={{ padding: '16px 24px' }}
            content={renderCustomLegend}
          />
        )}

        {_.map(chartConfig, (config, fieldName) => {
          if (!config || !_.includes(visibleChartComponents, config.field)) {
            return (
              <React.Fragment
                key={`portfolio-${chartTitle}-table-row-${fieldName}`}
              />
            );
          }

          const { chartType, color, format } = config;
          const reactChildKey = `${chartType}-${fieldName}`;

          // This switch-case unfortunately has to be written in-line
          // and return direct Recharts components.
          // Wrapping the chart component as a reusable React component will not render.
          // https://github.com/recharts/recharts/issues/2788
          switch (chartType) {
            case 'line':
              return (
                <Line
                  key={reactChildKey}
                  dataKey={fieldName}
                  fill={color}
                  stroke={color}
                  strokeWidth="2px"
                  dot
                  yAxisId={format}
                />
              );
            case 'bar':
            case 'dashedLine':
            case 'area':
            case 'none':
            default:
              return null;
          }
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
